import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import { Box, Flex } from 'reflexbox';
import { rgba } from 'polished';
import styled from 'styled-components';
import _ from 'lodash';

import * as graphql from 'generated/graphql';
import ArrowRightAboveIcon from 'packages/kakapo-ui-kit/assets/icons/ArrowRightAboveIcon';
import PlusIcon from 'packages/kakapo-ui-kit/assets/icons/PlusIcon';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import Color from '../../common/Color';
import Paragraph from '../../common/Paragraph';
import QuillText from '../../editors/QuillText';
import PopoverTarget from '../../popovers/PopoverTarget';

import GoalCreateCompetencyModal from './GoalCreateCompetencyModal';

const Container = styled(Box)<{ standalone?: boolean }>`
	background: ${(props) => props.theme.colors.whiteBlue};
	border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
	color: ${(props) => props.theme.colors.slateBlue};

	${(props) =>
		props.standalone
			? `
		border-radius: 4px;
		border-radius: 4px;

		> :last-child {
			border-radius: 4px;
			border-radius: 4px;
		}
	`
			: `
		border-top: none;

		border-bottom-left-radius: 4px;
		border-bottom-right-radius: 4px;

		> :last-child {
			border-bottom-left-radius: 4px;
			border-bottom-right-radius: 4px;
		}
	`}
`;

const Square = styled(Box)<{ rotated?: boolean }>`
	width: 20px;
	height: 20px;
	background: ${(props) => props.theme.colors.lightBlueDark};
	border-radius: 4px;

	svg {
		transform: ${(props) => (props.rotated ? 'scale(0.8, 0.8) rotateZ(45deg)' : 'scale(0.8, 0.8)')};
	}

	${(props) =>
		props.rotated &&
		`
		&:hover {
			background: ${rgba(props.theme.colors.slateBlue, 0.5)};

			&:active {
				background: ${props.theme.colors.slateBlue};
			}
		}
	`}
`;

const InputContainer = styled(Flex)``;

const Row = styled(Box)<{ interactive?: boolean; border?: false; standalone?: boolean }>`
	padding: 8px ${(props) => (props.standalone ? 8 : 18)}px;

	${(props) =>
		props.border !== false && `border-top: 1px solid ${props.theme.colors.lightBlueDark};`}

	${(props) =>
		props.interactive &&
		`
		cursor: pointer;

		&:hover {
			background: ${rgba(props.theme.colors.lightBlue, 0.25)};

			&:active {
				background: ${rgba(props.theme.colors.lightBlue, 0.5)};
			}
		}`};
`;

const AddButton = styled(Row)<{ standalone?: boolean }>`
	align-items: center;
	padding: 8px ${(props) => (props.standalone ? 8 : 18)}px;
	border-top: none;
`;

const Input = styled.input`
	background: transparent;
	padding: 8px 18px;
	padding-left: 0px;
	flex-grow: 1;
	font-size: 14px;
	color: ${(props) => props.theme.colors.slateBlue};

	::placeholder {
		color: ${(props) => props.theme.colors.lightBlueDark};
	}
`;

const Label = styled(Box)`
	line-height: 20px;
`;

const TooltipTitle = styled.div`
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
	padding-top: 10px;
	margin: 0px -20px;
	margin-top: 10px !important;
	margin-bottom: -5px;
	text-align: center;
`;

function GoalCreateCompetencySuggestion(props: {
	competency: graphql.Competency;
	onClick: () => void;
}) {
	const { localize } = useLocalize();

	return (
		<Row interactive onClick={props.onClick}>
			<Flex>
				<Box width={20} mr={2} flexGrow={0} flexShrink={0} />
				<Box>{localize(props.competency.title).text}</Box>
			</Flex>
		</Row>
	);
}

function GoalCreateCompetencyLevelOptionSelection(props: {
	option: graphql.CompetencyLevelOption;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	return (
		<Box flexShrink={0} flexGrow={0}>
			<PopoverTarget
				content={
					<Box style={{ padding: 20 }}>
						<Paragraph color="slateBlue">
							<QuillText
								quill={localize(props.option.criteria ?? [])?.text}
								placeholder={t('evaluation.submission.competency.levels.noDescription')}
							/>
						</Paragraph>
						<TooltipTitle>{localize(props.option.level.title)?.text}</TooltipTitle>
					</Box>
				}
			>
				<Flex alignItems="center">
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<ArrowRightAboveIcon color="slateBlue" />
					</Box>
					<Box flexGrow={0} flexShrink={0}>
						{localize(props.option.level.title ?? []).text}
					</Box>
				</Flex>
			</PopoverTarget>
		</Box>
	);
}

export default function GoalCreateCompetency(props: { standalone?: boolean; required?: boolean }) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [adding, setAdding] = useState(false);
	const [input, setInput] = useState('');
	const [selectedCompetency, setSelectedCompetency] = useState<graphql.Competency | undefined>();
	const [{ value: competency }, , { setValue: setCompetency }] = useField<
		graphql.Competency | undefined
	>('competency');
	const [{ value: competencyLevelInput }, , { setValue: setCompetencyLevelInput }] = useField<
		{ competency: number } | undefined
	>('competencyLevelInput');

	const competencyLevelOption =
		competencyLevelInput && competency?.levels[competencyLevelInput.competency];

	const query = graphql.useCompetenciesWithLevelsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const competencies = query.data?.competencies.edges.map(({ node }) => node as graphql.Competency);

	const matches =
		_.sortBy(
			competencies
				?.filter((competency) => competency.attachableToGoal)
				.filter((competency) =>
					localize(competency.title).text.toLowerCase().includes(input.toLowerCase()),
				) ?? [],
			(competency) => localize(competency.title).text.toLowerCase(),
		) ?? [];

	const reset = () => {
		setAdding(false);
		setInput('');
		setSelectedCompetency(undefined);
		setCompetency(undefined);
		setCompetencyLevelInput(undefined);
	};

	return (
		<Container standalone={props.standalone}>
			{competency && competencyLevelOption ? (
				<Row border={false} standalone={props.standalone}>
					<Flex alignItems="center">
						<Square
							mr={2}
							flexGrow={0}
							flexShrink={0}
							rotated
							style={{ cursor: 'pointer' }}
							onClick={reset}
						>
							<PlusIcon />
						</Square>
						<Box flexGrow={1} flexShrink={1}>
							{localize(competency.title).text}
						</Box>
						<GoalCreateCompetencyLevelOptionSelection option={competencyLevelOption} />
					</Flex>
				</Row>
			) : adding ? (
				<InputContainer>
					<Square
						mr={2}
						flexGrow={0}
						flexShrink={0}
						rotated
						style={{
							marginTop: 8,
							marginBottom: 8,
							marginLeft: props.standalone ? 8 : 18,
							cursor: 'pointer',
						}}
						onClick={reset}
					>
						<PlusIcon />
					</Square>
					<Input
						placeholder={t('goals.create.form.competency.startTyping')}
						autoFocus
						value={input}
						onChange={(event) => setInput(event.currentTarget.value)}
					/>
				</InputContainer>
			) : (
				<AddButton onClick={() => setAdding(true)} interactive standalone={props.standalone}>
					<Flex>
						<Square mr={2} flexGrow={0} flexShrink={0}>
							<PlusIcon />
						</Square>
						<Label>
							{t('goals.create.form.competency.placeholder')}
							{props.required ? null : (
								<>
									{' '}
									<Color color="lightBlueDark">
										({t('goals.create.form.competency.placeholderOptional')})
									</Color>
								</>
							)}
						</Label>
					</Flex>
				</AddButton>
			)}
			{adding && (
				<>
					{matches.map((match) => (
						<GoalCreateCompetencySuggestion
							key={match.id}
							competency={match as graphql.Competency}
							onClick={() => setSelectedCompetency(match as graphql.Competency)}
						/>
					))}
					{competencies && matches.length === 0 && input.length > 0 && (
						<Row>
							<Flex justifyContent="center">{t('goals.create.form.competency.noResults')}</Flex>
						</Row>
					)}
				</>
			)}
			{selectedCompetency && (
				<GoalCreateCompetencyModal
					competency={selectedCompetency}
					onCancel={reset}
					onSave={() => {
						setCompetency(selectedCompetency);
						setAdding(false);
						setSelectedCompetency(undefined);
					}}
				/>
			)}
		</Container>
	);
}
